import {
  INVITATION_ACCEPT_IMMEDIATELY_PARAM,
  INVITATION_TOKEN_PARAM,
  ORGANIZATION_ID_PARAM
} from 'configs';
import { InvitationQuery } from 'providers/query/useQueryParam';

const PENDING_INVITATION_STORAGE_KEY = 'pendingInvitation';

export const storePendingInvitation = (invitation: InvitationQuery): void => {
  if (!invitation.token || !invitation.organizationId) {
    return;
  }
  sessionStorage.setItem(
    PENDING_INVITATION_STORAGE_KEY,
    JSON.stringify(invitation)
  );
};

export const getStoredPendingInvitation = (): InvitationQuery | null => {
  const raw = sessionStorage.getItem(PENDING_INVITATION_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as InvitationQuery;
    if (parsed.token && parsed.organizationId) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

export const clearPendingInvitation = (): void => {
  sessionStorage.removeItem(PENDING_INVITATION_STORAGE_KEY);
};

export type ResolvePendingInvitationOptions = {
  /** Default true. When false, only URL/query params count (not sessionStorage). */
  includeStored?: boolean;
};

export const resolvePendingInvitation = (
  locationSearch: string,
  invitationQuery?: InvitationQuery,
  options?: ResolvePendingInvitationOptions
): InvitationQuery | null => {
  if (locationSearch.includes(INVITATION_TOKEN_PARAM)) {
    const params = new URLSearchParams(locationSearch);
    const token = params.get(INVITATION_TOKEN_PARAM) ?? undefined;
    const organizationId = params.get(ORGANIZATION_ID_PARAM) ?? undefined;
    if (token && organizationId) {
      return {
        token,
        organizationId,
        acceptImmediately:
          params.get(INVITATION_ACCEPT_IMMEDIATELY_PARAM) === 'true'
      };
    }
  }

  if (invitationQuery?.token && invitationQuery.organizationId) {
    return invitationQuery;
  }

  if (options?.includeStored === false) {
    return null;
  }

  return getStoredPendingInvitation();
};

export const buildInvitationSearch = (
  invitation: InvitationQuery,
  options?: { acceptImmediately?: boolean }
): string => {
  const params = new URLSearchParams();
  params.set(ORGANIZATION_ID_PARAM, invitation.organizationId ?? '');
  params.set(INVITATION_TOKEN_PARAM, invitation.token ?? '');
  if (options?.acceptImmediately || invitation.acceptImmediately) {
    params.set(INVITATION_ACCEPT_IMMEDIATELY_PARAM, 'true');
  }
  return `?${params.toString()}`;
};
